import { useState } from 'react';

import { Button } from '../../button';

import { Lab, Panel, Reset, Result } from './lab-parts';
import { ModelSelect } from './learning-model-surface';
import {
  ASSIGN_COMMAND,
  CODING_REFINEMENT,
  codingDone,
  codingReadLabel,
  codingSummary,
  codingTransition,
  codingTraces,
  currentEvent,
  duplicateCaught,
  filesChanged,
  firings,
  initialCoding,
  initialLimits,
  initialRun,
  initialTrigger,
  LIMITS,
  LIMITS_REFINEMENT,
  limitLabels,
  limitOutcome,
  limitSteps,
  limitsSummary,
  limitsTransition,
  permissionPrompts,
  recordRows,
  REQUESTS,
  requestLabels,
  RUN_IDS,
  RUN_REFINEMENT,
  runDone,
  runLabels,
  runsCreated,
  runSummary,
  runTransition,
  revealedCodingSteps,
  stepsStored,
  toLimit,
  toolsUsed,
  toRequest,
  toRunId,
  toTrigger,
  TRIGGER_REFINEMENT,
  triggerLabels,
  TRIGGERS,
  triggerSummary,
  triggerTransition,
  wentOut,
  type CodingAction,
  type LimitsAction,
  type RunAction,
  type TriggerAction,
} from './unattended-models';

import './relationship-labs.css';
import './unattended-labs.css';

function Predict({ children }: { children: string }) {
  return (
    <p className="rel-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}
function Table({
  label,
  columns,
  rows,
  template = `repeat(${columns.length}, minmax(0, 1fr))`,
}: {
  label: string;
  columns: string[];
  rows: (string | number)[][];
  template?: string;
}) {
  return (
    <div className="rel-table-scroll">
      <div className="rel-table cb-record-grid" role="table" aria-label={label}>
        <div
          role="row"
          className="rel-row rel-table-head"
          style={{ gridTemplateColumns: template }}
        >
          {columns.map((column) => (
            <span key={column} role="columnheader">
              {column}
            </span>
          ))}
        </div>
        {rows.length ? (
          rows.map((row, index) => (
            <div
              role="row"
              className="rel-row"
              key={index}
              style={{ gridTemplateColumns: template }}
            >
              {row.map((cell, column) => (
                <span role="cell" key={column}>
                  {cell}
                </span>
              ))}
            </div>
          ))
        ) : (
          <div role="row" className="rel-row">
            <span role="cell">No rows</span>
          </div>
        )}
      </div>
    </div>
  );
}
function Facts({ values }: { values: [string, string][] }) {
  return (
    <dl className="cb-memory">
      {values.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
const tokens = (value: number) => value.toLocaleString('en-US');
const RUN_CAPTION =
  'This graphic replays the digest and the assignment command on their run records.';
const LIMITS_CAPTION =
  'This graphic replays the real command, models the cost cap and the clock on its steps.';
const TRIGGER_CAPTION = 'This graphic simulates two firings of each trigger.';
const CODING_CAPTION =
  "This graphic illustrates the shape of a coding agent's loop rather than a real run.";

export function UnattendedRunLab() {
  const [state, setState] = useState(initialRun);
  const act = (action: RunAction) => setState((current) => runTransition(current, action));
  const rows = recordRows(state);
  const event = currentEvent(state);
  const summary = runSummary(state);
  return (
    <Lab
      title="What does a run record show as it runs?"
      eyebrow="Unattended runs · a run is a job"
      className="rel-lab unattended-lab"
    >
      <Predict>
        What does the run record show as it runs? Choose a Run and Crash after step 2, then press
        Advance.
      </Predict>
      <div className="cb-controls">
        <label>
          Run
          <ModelSelect
            aria-label="Run"
            value={state.run}
            onValueChange={(value) => act({ type: 'run', value: toRunId(value) })}
            options={RUN_IDS.map((id) => ({ value: id, label: runLabels[id] }))}
          />
        </label>
        <Button
          size="sm"
          variant="secondary"
          aria-pressed={state.crash}
          onClick={() => act({ type: 'toggle-crash' })}
        >
          Crash after step 2
        </Button>
        <Button size="sm" disabled={runDone(state)} onClick={() => act({ type: 'advance' })}>
          Advance
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Table
        label="The run record"
        columns={['Row', 'What the row says', 'Status', 'Prompt tokens']}
        template="minmax(0, 0.4fr) minmax(0, 2.6fr) minmax(0, 0.8fr) minmax(0, 0.9fr)"
        rows={rows.map((entry, index) => [
          index,
          entry.row,
          entry.status,
          tokens(entry.promptTokens),
        ])}
      />
      <Facts
        values={[
          ['Status', event.status],
          ['Steps stored', String(stepsStored(state))],
          ['Prompt tokens', tokens(event.promptTokens)],
        ]}
      />
      <Result tone={summary.tone}>{summary.message}</Result>
      {runDone(state) ? <p className="rel-caption">{RUN_REFINEMENT}</p> : null}
      <p className="rel-caption">{RUN_CAPTION}</p>
    </Lab>
  );
}

export function UnattendedLimitsLab() {
  const [state, setState] = useState(initialLimits);
  const act = (action: LimitsAction) => setState((current) => limitsTransition(current, action));
  const steps = limitSteps(state.limit);
  const outcome = limitOutcome(state.limit);
  const summary = limitsSummary(state);
  return (
    <Lab
      title="Which limit stops the run, and what does it leave?"
      eyebrow="Unattended runs · limits for a run nobody watches"
      className="rel-lab unattended-lab"
    >
      <Predict>
        Which limit stops the run, and what does it leave? Choose a Limit, then press Run the
        assignment command.
      </Predict>
      <div className="cb-controls">
        <label>
          Limit
          <ModelSelect
            aria-label="Limit"
            value={state.limit}
            onValueChange={(value) => act({ type: 'limit', value: toLimit(value) })}
            options={LIMITS.map((id) => ({ value: id, label: limitLabels[id] }))}
          />
        </label>
        <Button size="sm" disabled={state.ran} onClick={() => act({ type: 'run' })}>
          Run the assignment command
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="The command">
        <pre aria-label="The command">{ASSIGN_COMMAND}</pre>
      </Panel>
      <Table
        label="The steps"
        columns={['Step', 'What ran', 'Prompt tokens so far', 'Clock']}
        template="minmax(0, 0.4fr) minmax(0, 2.2fr) minmax(0, 1fr) minmax(0, 0.8fr)"
        rows={
          state.ran
            ? steps.map((entry) => [
                entry.step,
                entry.canceled ? `${entry.what}, canceled` : entry.what,
                tokens(entry.promptTokens),
                `about ${entry.seconds} s`,
              ])
            : []
        }
      />
      <Panel label="What the run left">
        <pre aria-label="What the run left">{state.ran ? outcome.sentence : '(not run yet)'}</pre>
      </Panel>
      {state.ran ? (
        <Facts
          values={[
            ['Stopped by', outcome.stoppedBy],
            ['Prompt tokens', tokens(outcome.promptTokens)],
            ['Left pending', outcome.pending],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.ran ? <p className="rel-caption">{LIMITS_REFINEMENT}</p> : null}
      <p className="rel-caption">{LIMITS_CAPTION}</p>
    </Lab>
  );
}

export function UnattendedTriggerLab() {
  const [state, setState] = useState(initialTrigger);
  const act = (action: TriggerAction) => setState((current) => triggerTransition(current, action));
  const summary = triggerSummary(state);
  return (
    <Lab
      title="What happens when the same event arrives twice?"
      eyebrow="Unattended runs · scheduled and event triggers"
      className="rel-lab unattended-lab"
    >
      <Predict>
        What happens when the same event arrives twice? Choose a Trigger and Idempotency key, then
        press Fire twice.
      </Predict>
      <div className="cb-controls">
        <label>
          Trigger
          <ModelSelect
            aria-label="Trigger"
            value={state.trigger}
            onValueChange={(value) => act({ type: 'trigger', value: toTrigger(value) })}
            options={TRIGGERS.map((id) => ({ value: id, label: triggerLabels[id] }))}
          />
        </label>
        <Button
          size="sm"
          variant="secondary"
          aria-pressed={state.key}
          onClick={() => act({ type: 'toggle-key' })}
        >
          Idempotency key
        </Button>
        <Button size="sm" disabled={state.fired} onClick={() => act({ type: 'fire' })}>
          Fire twice
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Table
        label="The firings"
        columns={['Firing', 'At', 'Key', 'Outcome']}
        template="minmax(0, 0.5fr) minmax(0, 1.3fr) minmax(0, 1.3fr) minmax(0, 1.6fr)"
        rows={
          state.fired
            ? firings(state.trigger, state.key).map((firing) => [
                firing.firing,
                firing.at,
                firing.key,
                firing.outcome,
              ])
            : []
        }
      />
      {state.fired ? (
        <Facts
          values={[
            ['Runs created', String(runsCreated(state.trigger, state.key))],
            ['Duplicate caught', duplicateCaught(state.trigger, state.key)],
            ['What went out', wentOut(state.trigger, state.key)],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.fired ? <p className="rel-caption">{TRIGGER_REFINEMENT}</p> : null}
      <p className="rel-caption">{TRIGGER_CAPTION}</p>
    </Lab>
  );
}

export function UnattendedCodingLab() {
  const [state, setState] = useState(initialCoding);
  const act = (action: CodingAction) => setState((current) => codingTransition(current, action));
  const trace = codingTraces[state.request];
  const done = codingDone(state);
  const summary = codingSummary(state);
  return (
    <Lab
      title="What does a coding agent's trace look like?"
      eyebrow="Unattended runs · coding agents are the same loop"
      className="rel-lab unattended-lab"
    >
      <Predict>
        What does a coding agent&apos;s trace look like? Choose a Request, then press Read from the
        top.
      </Predict>
      <div className="cb-controls">
        <label>
          Request
          <ModelSelect
            aria-label="Request"
            value={state.request}
            onValueChange={(value) => act({ type: 'request', value: toRequest(value) })}
            options={REQUESTS.map((id) => ({ value: id, label: requestLabels[id] }))}
          />
        </label>
        <Button size="sm" disabled={done} onClick={() => act({ type: 'read' })}>
          {codingReadLabel(state)}
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Table
        label="The trace"
        columns={['Step', 'Tool', 'What happened', 'Result']}
        template="minmax(0, 0.4fr) minmax(0, 0.8fr) minmax(0, 1.8fr) minmax(0, 1.8fr)"
        rows={revealedCodingSteps(state).map((entry, index) => [
          index + 1,
          entry.tool,
          entry.what,
          entry.result,
        ])}
      />
      {done ? (
        <Facts
          values={[
            ['Tools used', toolsUsed(trace)],
            ['Permission prompts', String(permissionPrompts(trace))],
            ['Files changed', String(filesChanged(trace))],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {done ? <p className="rel-caption">{CODING_REFINEMENT}</p> : null}
      <p className="rel-caption">{CODING_CAPTION}</p>
    </Lab>
  );
}
